/* eslint-disable no-undef */

import React, {PureComponent} from 'react'
import {withStyles} from '@material-ui/core/styles/index'

/**
 * Logo configuration array
 * Each logo has: src, alt, width, and optional marginBottom for fine-tuning vertical alignment
 * Add new logos here - the carousel will automatically include them
 */
const LOGO_CONFIG = [
    {
        src: 'http://uusi.kilpailu.fi/img/references/dna.svg',
        alt: 'DNA',
        width: 40
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/elisa.svg',
        alt: 'Elisa',
        width: 50
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/hartwall.svg',
        alt: 'Hartwall',
        width: 80
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/kymppi-katto.png',
        alt: 'Kymppi-Katto',
        width: 70
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/nutraq.png',
        alt: 'Nutraq',
        width: 60
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/oomi.svg',
        alt: 'Oomi',
        width: 55
    },
    {
        src: 'http://uusi.kilpailu.fi/img/references/vattenfall.svg',
        alt: 'Vattenfall',
        width: 90
    }
]

// Gap between logos in pixels
const LOGO_GAP = 48

// Duplicate logos for seamless infinite scroll (computed once)
const duplicatedLogos = [...LOGO_CONFIG, ...LOGO_CONFIG, ...LOGO_CONFIG]

// Base styles (theme-based, for all layouts except 'full')
const baseStyles = theme => ({
    carouselContainer: {
        width: '100%',
        overflow: 'hidden',
        paddingTop: 10,
        paddingBottom: 5,
        position: 'relative',
        // Fade edges for seamless infinite scroll effect
        maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)'
    },
    carouselTitle: {
        color: '#7a8a82',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontSize: '11px',
        textAlign: 'center',
        minWidth: '100%',
        marginTop: 5,
    },
    carouselTrack: {
        display: 'flex',
        alignItems: 'center',
        gap: LOGO_GAP,
        width: 'fit-content',
        animation: '$scroll 20s linear infinite'
    },
    logoItem: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    logoImg: {
        filter: 'grayscale(100%)',
        opacity: 0.45,
        transition: 'opacity 0.3s ease',
        objectFit: 'contain'
    },
    '@keyframes scroll': {
        '0%': {
            transform: 'translateX(0)'
        },
        '100%': {
            // Move by exactly one-third (one full set of logos) for seamless loop with 3 copies
            transform: 'translateX(-33.333%)'
        }
    }
})

// Enhanced styles (for layout='full' only)
const enhancedStyles = theme => ({
    carouselContainer: {
        width: '100%',
        overflow: 'hidden',
        paddingTop: 14,
        paddingBottom: 8,
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(32, 90, 64, 0.02) 0%, rgba(32, 90, 64, 0.04) 100%)',
        borderRadius: 12,
        marginTop: 16,
        // Fade edges for seamless infinite scroll effect
        maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
    },
    carouselTitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: '#6a7d72',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        fontSize: '10px',
        fontWeight: 600,
        textAlign: 'center',
        minWidth: '100%',
        marginTop: 0,
        marginBottom: 4,
    },
    carouselTrack: {
        display: 'flex',
        alignItems: 'center',
        gap: LOGO_GAP + 8,
        width: 'fit-content',
        animation: '$scroll 22s linear infinite'
    },
    logoItem: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    logoImg: {
        filter: 'grayscale(100%)',
        opacity: 0.5,
        transition: 'opacity 0.3s ease, filter 0.3s ease',
        objectFit: 'contain',
        '&:hover': {
            opacity: 0.7,
            filter: 'grayscale(50%)'
        }
    },
    '@keyframes scroll': {
        '0%': {
            transform: 'translateX(0)'
        },
        '100%': {
            // Move by exactly one-third (one full set of logos) for seamless loop with 3 copies
            transform: 'translateX(-33.333%)'
        }
    }
})

class QuestionPageFooter extends PureComponent {
    render() {
        const {classes} = this.props

        return (
            <div>
                <div className={classes.carouselTitle}>
                    Yhteistyössä
                </div>
                <div className={classes.carouselContainer + ' footer-logos'}>
                    <div className={classes.carouselTrack}>
                        {duplicatedLogos.map((logo, index) => (
                            <div key={`${logo.alt}-${index}`} className={classes.logoItem}>
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    className={classes.logoImg}
                                    style={{
                                        width: logo.width,
                                        opacity: logo.opacity || 0.45
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }
}

// Pre-create styled components at module level to prevent re-renders
const QuestionPageFooterBase = withStyles(baseStyles)(QuestionPageFooter)
const QuestionPageFooterEnhanced = withStyles(enhancedStyles)(QuestionPageFooter)

// Style switcher - returns pre-created components to prevent re-mounting
const QuestionPageFooterWithStyles = (props) => {
    const { layout, ...rest } = props;
    
    if (layout === 'full') {
        return <QuestionPageFooterEnhanced {...rest} />;
    }
    return <QuestionPageFooterBase {...rest} />;
};

export default QuestionPageFooterWithStyles;
